import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from 'services/organization/organization.service';
import { PublicRegistry } from 'services/organization/organization.interface';
import { ComputedFrom } from '@certemy/common';
import { ResizeService } from 'services/resize/resize.service';
import {
  PUBLIC_REPORT_PARAM_NAME,
  REPORT_FORMAT,
  REPORT_TYPE,
} from 'features/report-generate/report-generate.constants';
import { GenerateReportOption } from 'features/report-generate/report-generate.interface';
import { ReportPrintExportOptionsComponent } from 'features/report-generate/report-print-export-options/report-print-export-options.component';
import { REPORT_OPTION } from 'features/report-generate/report-print-export-options/report-print-export-options.constants';
import moment from 'moment-mini';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';
import { defer } from 'rxjs';

const DATE_FORMAT = 'MM/DD/YYYY hh:mm A';

@UntilDestroy()
@Component({
  selector: 'cm-public-registry-record',
  templateUrl: './public-registry-record.component.html',
  styleUrls: ['./public-registry-record.component.scss'],
})
export class PublicRegistryRecordComponent implements OnInit, OnDestroy {
  entries: PublicRegistry.RegistryEntry[] = [];
  registryId: string;
  organization: PublicRegistry.OrganizationInfo;
  isDesktop = true;
  showedAll = false;
  preview = false;
  private date = this.getDate();

  @ViewChild(ReportPrintExportOptionsComponent)
  private reportPrintExportOptionsComponent: ReportPrintExportOptionsComponent;

  constructor(
    private route: ActivatedRoute,
    private organizationService: OrganizationService,
    private resizeService: ResizeService,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {
    this.resizeService.resize$.pipe(untilDestroyed(this)).subscribe((props) => {
      this.isDesktop = props.isDesktop;
    });
  }

  ngOnInit() {
    const [{ result, organization }, registryId] = this.route.snapshot.data.record;
    this.entries = result;
    this.organization = organization;
    this.registryId = registryId;
    this.preview = this.router.url.includes('/public-registry/preview');
  }

  ngOnDestroy() {}

  @ComputedFrom('firstRow')
  get professionalFullName(): string {
    if (!this.firstRow) {
      return '';
    }
    const { firstName, lastName } = this.firstRow;
    return `${firstName} ${lastName}`;
  }

  @ComputedFrom('entries')
  get firstRow(): PublicRegistry.RegistryEntry {
    if (!this.entries.length) {
      return null;
    }
    return this.entries[0];
  }

  @ComputedFrom('firstRow', 'entries', 'date', 'showedAll')
  get reportQuery(): GenerateReportOption {
    const additionalParams = {
      uuid: this.registryId,
      number_instance_id: this.firstRow.id,
      [PUBLIC_REPORT_PARAM_NAME]: true,
      date: this.date,
    };
    if (this.showedAll) {
      additionalParams['profile_id'] = this.firstRow.profileId;
    }
    return {
      reportFormat: REPORT_FORMAT.PDF,
      reportType: this.preview ? REPORT_TYPE.PREVIEW_REGISTRY : REPORT_TYPE.PUBLIC_REGISTRY,
      additionalParams,
    };
  }

  showAll() {
    return defer(() =>
      this.preview
        ? this.organizationService.getPreviewOrganizationRegistryByProfileId(this.registryId, this.entries[0].profileId)
        : this.organizationService.getPublicOrganizationRegistryByProfileId(this.registryId, this.entries[0].profileId),
    )
      .pipe(take(1))
      .subscribe((response: PublicRegistry.ResponseRowData) => {
        this.showedAll = true;
        this.entries = [...this.entries, ...response.result.filter(({ id }) => id !== this.firstRow.id)];
      });
  }

  onBackClick() {
    this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  private getDate() {
    return moment(new Date()).format(DATE_FORMAT);
  }

  print() {
    this.date = this.getDate();
    this.cd.detectChanges();
    this.reportPrintExportOptionsComponent.onOptionSelect(REPORT_OPTION.PRINT);
  }
}
